import labels from "../../utils/labels";
import StandardButton from "../UI/buttons/StandardButton";
import LabeledSection from "../UI/LabeledSection";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { EmployeeTransport, TransportationType } from "../../types/ganttChart";
import { useEffect, useMemo, useRef, useState } from "react";
import EmployeeTransportListItem from "./EmployeeTransportListItem";
import timestampService from "../../service/timestampService";
import TransportValidationUtil from "../../utils/transportValidationUtil";
import EmployeeSettingsService from "../../service/employeeSettingsService";
import {
  clearEmployeeTransportEdit,
  editEmployeeTransportType,
} from "../../store/employeeSettings";

const EmployeeTransportType = ({
  onUnsavedEdit,
}: {
  onUnsavedEdit: (hasChanges: boolean) => void;
}) => {
  const {
    transportationType,
    employeeWorkTime,
    employeeShiftId,
    manualShiftChanges,
  } = useAppSelector((state) => state.drawerSlice.selectedEmployee!);
  // const [localTransports, setLocalTransports] = useState<EmployeeTransport[]>([])  // toggle back on multi-transports
  // const initialTransportRef = useRef<EmployeeTransport[]>([])   // toggle back on multi-transports
  const [localTransport, setLocalTransport] = useState<EmployeeTransport>();
  const initialTransportRef = useRef<EmployeeTransport>();

  const dispatch = useAppDispatch();
  const [isPending, setIsPending] = useState(false);
  const { manualEdits } = useAppSelector(
    (state) => state.employeeSettingsSlice,
  );

  const manualTransportTypeEdits =
    manualEdits[employeeShiftId]?.transportationType ??
    manualShiftChanges?.transportationType;
  const transportToUse = manualTransportTypeEdits ?? transportationType!;

  // toggle back on multi-transports:
  // useEffect(() => {
  //   const mappedTransports: EmployeeTransport[] = transportToUse.map((transport) => ({
  //     ...transport,
  //     timeWindow: {
  //       transportStartTime: timestampService.getHoursAndMinutesTimestamp(
  //         transport.timeWindow.transportStartTime
  //       ),
  //       transportEndTime: timestampService.getHoursAndMinutesTimestamp(
  //         transport.timeWindow.transportEndTime
  //       )
  //     }
  //   }))
  //   setLocalTransports(mappedTransports)
  //   initialTransportRef.current = mappedTransports
  // }, [transportToUse])

  useEffect(() => {
    const manualWorkTimeEdits =
      manualEdits[employeeShiftId]?.employeeWorkTime ??
      manualShiftChanges?.employeeWorkTime;
    const workTimeToUse = manualWorkTimeEdits ?? employeeWorkTime;

    if (!workTimeToUse?.workTimeStart || !workTimeToUse?.workTimeEnd) return;

    const mappedTransport: EmployeeTransport = {
      type: transportToUse,
      timeWindow: {
        transportStartTime: timestampService.getHoursAndMinutesTimestamp(
          workTimeToUse.workTimeStart,
        ),
        transportEndTime: timestampService.getHoursAndMinutesTimestamp(
          workTimeToUse.workTimeEnd,
        ),
      },
    };
    setLocalTransport((previous) => {
      if (!previous) return mappedTransport;
      const locallyChangedType = previous.type !== transportToUse;
      return locallyChangedType
        ? { ...mappedTransport, type: previous.type }
        : mappedTransport;
    });
    initialTransportRef.current = mappedTransport;
  }, [
    transportToUse,
    employeeWorkTime,
    manualEdits,
    manualShiftChanges,
    employeeShiftId,
  ]);

  // getting all transport options
  const getTransportTitle = (type: TransportationType) => {
    switch (type) {
      case "CAR":
        return labels.transportCar;
      case "BIKE":
        return labels.transportBike;
      case "ELECTRIC_BIKE":
        return labels.transportElectricBike;
      case "WALK":
        return labels.transportWalking;
    }
  };
  const transportOptions: TransportationType[] = [
    "CAR",
    "BIKE",
    "ELECTRIC_BIKE",
    "WALK",
  ];
  const options = useMemo(() => {
    return transportOptions.map((type) => ({
      id: type,
      title: getTransportTitle(type),
    }));
  }, []);

  // toggle back on multi-transports:
  // const hasMultipleTransports = localTransports.length > 1;
  // const hasChanges =
  //   JSON.stringify(initialTransportRef.current) !== JSON.stringify(localTransports);

  // for one transport type
  const hasChanges = localTransport?.type !== initialTransportRef.current?.type;

  useEffect(() => {
    onUnsavedEdit(hasChanges);
  }, [onUnsavedEdit, hasChanges]);

  // toggle back on multi-transports (add/delete/edit):

  // // add local transport
  // const addTransport = () => {
  //   setLocalTransports((previousTransports) => [
  //     ...previousTransports,
  //     {
  //       type: "",
  //       timeWindow: { transportStartTime: "00:00", transportEndTime: "00:00" },
  //     },
  //   ]);
  // };

  // // delete local transport
  // const deleteTransport = (transportIndex: number) => {
  //   if (hasMultipleTransports) {
  //     setLocalTransports((transports) =>
  //       transports.filter((_, index) => index !== transportIndex),
  //     );
  //   }
  // };

  // // edit local transport
  // const updateTransport = (
  //   transportIndex: number,
  //   updates: {
  //     type?: TransportationType;
  //     transportStartTime?: string;
  //     transportEndTime?: string;
  //   },
  // ) => {
  //   setLocalTransports((transports) =>
  //     transports.map((transport, index) =>
  //       index === transportIndex
  //         ? {
  //             type: updates.type ?? transport.type,
  //             timeWindow: {
  //               transportStartTime:
  //                 updates.transportStartTime ??
  //                 transport.timeWindow.transportStartTime,
  //               transportEndTime:
  //                 updates.transportEndTime ??
  //                 transport.timeWindow.transportEndTime,
  //             },
  //           }
  //         : transport,
  //     ),
  //   );
  // };

  // edit local transport - for one transport type
  const updateTransport = (newType: TransportationType) => {
    if (!localTransport) return;
    setLocalTransport({
      ...localTransport,
      type: newType,
    });
  };

  // input validation - toggle back on multi-transports:

  // // empty fields
  // const hasEmptyFields = localTransports.some((transport) => {
  //   return (
  //     !transport.type ||
  //     transport.timeWindow.transportStartTime === "00:00" ||
  //     transport.timeWindow.transportEndTime === "00:00"
  //   );
  // });

  // const shiftStartTimeMinutes = timestampService.timeToMinutesSum(employeeWorkTime.workTimeStart);
  // const shiftEndTimeMinutes = timestampService.timeToMinutesSum(employeeWorkTime.workTimeEnd);

  // const localTimeIntervals = localTransports.map((transport) => ({
  //   startMinutes: timestampService.timeToMinutesSum(
  //     transport.timeWindow.transportStartTime,
  //   ),
  //   endMinutes: timestampService.timeToMinutesSum(
  //     transport.timeWindow.transportEndTime,
  //   ),
  // }));

  // // transport time is outside workhours
  // const isOutsideWorkHours =
  //   !hasEmptyFields &&
  //   TransportValidationUtil.hasOutsideWorkHours(
  //     localTimeIntervals,
  //     shiftStartTimeMinutes,
  //     shiftEndTimeMinutes,
  //   );

  // // transport times are overlapping
  // const hasOverlap =
  //   !hasEmptyFields && TransportValidationUtil.hasOverlap(localTimeIntervals);

  // // transport time doesn't fill workhours
  // const totalTransportMinutes = !hasEmptyFields
  //   ? TransportValidationUtil.getTotalMinutes(localTimeIntervals)
  //   : 0;

  // const worktimeMinutes = TransportValidationUtil.getWorktimeMinutes(
  //   shiftStartTimeMinutes,
  //   shiftEndTimeMinutes,
  // );

  // const hasWorktimeGap =
  //   !hasEmptyFields &&
  //   !isOutsideWorkHours &&
  //   !hasOverlap &&
  //   totalTransportMinutes < worktimeMinutes;

  // let transportErrorMessage = null;
  // if (hasEmptyFields) {
  //   transportErrorMessage = labels.errorEmptyFields;
  // } else if (isOutsideWorkHours) {
  //   transportErrorMessage = labels.errorOutsideWorkHours;
  // } else if (hasOverlap) {
  //   transportErrorMessage = labels.errorOverlappingTimeWindow;
  // } else if (hasWorktimeGap) {
  //   transportErrorMessage = labels.errorNotFilledWorkHours;
  // } else {
  //   transportErrorMessage = null;
  // }

  // only for one transport type:
  const transportErrorMessage = null;

  // toggle back on multi-transports:
  // // save transport changes
  // async function handleSaveTransportChanges() {
  //   const updatedTransports: EmployeeTransport[] = localTransports.map(
  //     (transport) => ({
  //       ...transport,
  //       timeWindow: {
  //         transportStartTime: timestampService.mergeTimeIntoDate(
  //           employeeWorkTime.workTimeStart,
  //           transport.timeWindow.transportStartTime,
  //         ),
  //         transportEndTime: timestampService.mergeTimeIntoDate(
  //           employeeWorkTime.workTimeEnd,
  //           transport.timeWindow.transportEndTime,
  //         ),
  //       },
  //     }),
  //   );
  //   const matchesBaseTransport =
  //     JSON.stringify(updatedTransports) === JSON.stringify(transportationType);
  //   setIsPending(true);
  //   try {
  //     await EmployeeSettingsService.editEmployeeTransportType(employeeShiftId, updatedTransports, dispatch);
  //     dispatch(editEmployeeTransportType({
  //       shiftId: employeeShiftId,
  //       transportationType: updatedTransports,
  //     }));
  //     if (matchesBaseTransport) dispatch(clearEmployeeTransportEdit({ shiftId: employeeShiftId }));
  //     initialTransportRef.current = localTransports;
  //     setIsPending(false);
  //   } catch {
  //     setLocalTransports(initialTransportRef.current);
  //     setIsPending(false);
  //   }
  // }

  // save transport changes - for one transport type
  async function handleSaveTransportChanges() {
    if (!localTransport) return;
    const updatedTransportType = localTransport.type;
    const matchesBaseTransport = updatedTransportType === transportationType;
    setIsPending(true);
    try {
      await EmployeeSettingsService.editEmployeeTransportType(
        employeeShiftId,
        updatedTransportType,
        dispatch,
      );
      dispatch(
        editEmployeeTransportType({
          shiftId: employeeShiftId,
          transportationType: updatedTransportType,
        }),
      );
      if (matchesBaseTransport)
        dispatch(clearEmployeeTransportEdit({ shiftId: employeeShiftId }));
      initialTransportRef.current = localTransport;
      setIsPending(false);
    } catch {
      setLocalTransport(initialTransportRef.current);
      setIsPending(false);
    }
  }

  const handleDiscardTransportChanges = () => {
    setLocalTransport(initialTransportRef.current); // change back to setLocalTransports on multi-transports
  };

  return (
    <LabeledSection
      title={labels.transport}
      titleSize="h4"
      spacing="small"
      // toggle back on multi-transports:
      // extraOptions={
      //   <StandardButton
      //     ariaLabel={labels.addTransportType}
      //     variant="TERTIARY"
      //     onClick={addTransport}
      //     classes="!h-[17.5px]"
      //   >
      //     {labels.addTransportType}
      //   </StandardButton>
      // }
    >
      {/* toggle back on multi-transports: */}
      {/* array of transports */}
      {/* {localTransports.map((transport, index) => (
        <EmployeeTransportListItem
          key={`${transport.type}-${index}`}
          transport={transport}
          options={options}
          onTypeChange={(newOption) =>
            updateTransport(index, { type: newOption })
          }
          onStartChange={(newStart) =>
            updateTransport(index, { transportStartTime: newStart })
          }
          onEndChange={(newEnd) =>
            updateTransport(index, { transportEndTime: newEnd })
          }
          canDelete={hasMultipleTransports}
          onDelete={() => deleteTransport(index)}
        />
      ))} */}

      {/* render one transport type */}
      {localTransport && (
        <EmployeeTransportListItem
          key={localTransport.type}
          transport={localTransport}
          options={options}
          onTypeChange={(newOption) => updateTransport(newOption)}
          onStartChange={() => {}}
          onEndChange={() => {}}
          canDelete={false}
          onDelete={() => {}}
          disableTimeWindow
        />
      )}

      {/* error messages */}
      {transportErrorMessage && (
        <p className="flex justify-end text-[var(--col-error-4)]">
          {transportErrorMessage}
        </p>
      )}

      {/* controls for when the user edits */}
      {hasChanges && (
        <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
          <StandardButton
            ariaLabel={labels.discard}
            variant="SECONDARY"
            classes="w-fit"
            onClick={handleDiscardTransportChanges}
          >
            {labels.discard}
          </StandardButton>

          <StandardButton
            ariaLabel={labels.save}
            variant="PRIMARY"
            classes="w-fit"
            onClick={handleSaveTransportChanges}
            disable={!!transportErrorMessage}
            pending={isPending}
          >
            {labels.save}
          </StandardButton>
        </div>
      )}
    </LabeledSection>
  );
};

export default EmployeeTransportType;
